import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

// VH-55: Timeframe selector — date pickers + quick preset chips
// Default preset: last 90 days. Validates from <= to, to <= today, range <= 5 years.

const MS_PER_DAY = 24 * 60 * 60 * 1000
const MAX_RANGE_DAYS = 5 * 365 // 5 years sanity cap
const WIDE_WARNING_DAYS = 365 // > 1 year shows the "consider 90 days" warning

/** Format a Date as YYYY-MM-DD in local time (avoids UTC off-by-one). */
function toISODate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function presetRange(presetId) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = new Date(today)

  switch (presetId) {
    case '30d':
      start.setDate(today.getDate() - 30)
      return { start, end: today }
    case '90d':
      start.setDate(today.getDate() - 90)
      return { start, end: today }
    case '6mo':
      start.setMonth(today.getMonth() - 6)
      return { start, end: today }
    case '1yr':
      start.setFullYear(today.getFullYear() - 1)
      return { start, end: today }
    case 'ytd':
      return { start: new Date(today.getFullYear(), 0, 1), end: today }
    case 'all':
      // Hard cap at 5 years to satisfy the validation; backend may further cap.
      start.setFullYear(today.getFullYear() - 5)
      return { start, end: today }
    default:
      start.setDate(today.getDate() - 90)
      return { start, end: today }
  }
}

const PRESETS = [
  { id: '30d', labelKey: 'aiAnalysis.timeframe.preset30d' },
  { id: '90d', labelKey: 'aiAnalysis.timeframe.preset90d' },
  { id: '6mo', labelKey: 'aiAnalysis.timeframe.preset6mo' },
  { id: '1yr', labelKey: 'aiAnalysis.timeframe.preset1yr' },
  { id: 'ytd', labelKey: 'aiAnalysis.timeframe.presetYTD' },
  { id: 'all', labelKey: 'aiAnalysis.timeframe.presetAll' },
]

export default function TimeframeSelector({ value, onChange, disabled = false }) {
  const { t } = useTranslation()

  // Initial defaults: last 90 days.
  // Deliberately computed once, from the value as it was at mount — later
  // changes to `value` must NOT reset the user's in-progress selection. A lazy
  // useState initializer states that intent directly; the previous useMemo with
  // an empty dep array relied on a memo cache React is free to discard, and
  // read `value` without declaring it.
  const [initial] = useState(() => {
    if (value?.start_date && value?.end_date) {
      return { start_date: value.start_date, end_date: value.end_date }
    }
    const { start, end } = presetRange('90d')
    return { start_date: toISODate(start), end_date: toISODate(end) }
  })

  const [startDate, setStartDate] = useState(initial.start_date)
  const [endDate, setEndDate] = useState(initial.end_date)
  const [activePreset, setActivePreset] = useState('90d')

  // Push initial value up so parent always has a value
  useEffect(() => {
    onChange?.({ start_date: initial.start_date, end_date: initial.end_date, valid: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const todayISO = useMemo(() => toISODate(new Date()), [])

  // Validation
  const validation = useMemo(() => {
    if (!startDate || !endDate) {
      return { valid: false, error: null }
    }
    if (startDate > endDate) {
      return { valid: false, error: t('aiAnalysis.timeframe.errorOrder') }
    }
    if (endDate > todayISO) {
      return { valid: false, error: t('aiAnalysis.timeframe.errorFuture') }
    }
    const days = Math.floor((new Date(endDate) - new Date(startDate)) / MS_PER_DAY)
    if (days > MAX_RANGE_DAYS) {
      return { valid: false, error: t('aiAnalysis.timeframe.errorMaxRange') }
    }
    return { valid: true, error: null, days }
  }, [startDate, endDate, todayISO, t])

  const days = validation.days ?? 0
  const showWideWarning = validation.valid && days > WIDE_WARNING_DAYS

  // Notify parent on changes
  useEffect(() => {
    onChange?.({
      start_date: startDate,
      end_date: endDate,
      valid: validation.valid,
      error: validation.error,
      days
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, validation.valid, validation.error, days])

  const handlePresetClick = (presetId) => {
    const { start, end } = presetRange(presetId)
    setStartDate(toISODate(start))
    setEndDate(toISODate(end))
    setActivePreset(presetId)
  }

  const handleStartChange = (val) => {
    setStartDate(val)
    setActivePreset(null)
  }

  const handleEndChange = (val) => {
    setEndDate(val)
    setActivePreset(null)
  }

  return (
    <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
      <div className="flex items-center text-sm font-medium text-gray-700">
        <CalendarIcon className="h-4 w-4 mr-2" />
        {t('aiAnalysis.timeframe.title')}
      </div>

      {/* Preset chips */}
      <div>
        <p className="text-xs text-gray-500 mb-1.5">
          {t('aiAnalysis.timeframe.presets')}
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => handlePresetClick(preset.id)}
              disabled={disabled}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                activePreset === preset.id
                  ? 'bg-purple-600 text-on-neutral'
                  : 'bg-surface-container text-gray-700 border border-gray-300 hover:bg-gray-100'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {t(preset.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Date pickers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="vh55-from" className="block text-xs font-medium text-gray-600 mb-1">
            {t('aiAnalysis.timeframe.from')}
          </label>
          <input
            id="vh55-from"
            type="date"
            value={startDate}
            max={endDate || todayISO}
            onChange={(e) => handleStartChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label htmlFor="vh55-to" className="block text-xs font-medium text-gray-600 mb-1">
            {t('aiAnalysis.timeframe.to')}
          </label>
          <input
            id="vh55-to"
            type="date"
            value={endDate}
            min={startDate}
            max={todayISO}
            onChange={(e) => handleEndChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Validation error */}
      {validation.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-red-700 text-xs flex items-start">
          <ExclamationTriangleIcon className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" />
          <span>{validation.error}</span>
        </div>
      )}

      {/* Wide-window warning */}
      {showWideWarning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-2 text-yellow-800 text-xs flex items-start">
          <ExclamationTriangleIcon className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" />
          <span>{t('aiAnalysis.timeframe.warningWide')}</span>
        </div>
      )}
    </div>
  )
}
